import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { getDateOption } from './utils';
export function useFilterTags(props) {
  var _props$defaultFilterI = props.defaultFilterItems,
    defaultFilterItems = _props$defaultFilterI === void 0 ? [] : _props$defaultFilterI,
    onSearch = props.onSearch,
    onFilterChange = props.onFilterChange,
    onTagRemove = props.onTagRemove,
    onClearAll = props.onClearAll,
    onSave = props.onSave,
    _props$labelKey = props.labelKey,
    labelKey = _props$labelKey === void 0 ? 'label' : _props$labelKey,
    _props$codeKey = props.codeKey,
    codeKey = _props$codeKey === void 0 ? 'code' : _props$codeKey,
    dateFormatString = props.dateFormatString;
  var intl = useIntl();
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchText = _useState2[0],
    setSearchText = _useState2[1];
  var _useState3 = useState(defaultFilterItems),
    _useState4 = _slicedToArray(_useState3, 2),
    filterItems = _useState4[0],
    setFilterItems = _useState4[1];
  var createSearchTag = function createSearchTag(searchText) {
    var searchTag = {
      type: 'search',
      filterName: 'search',
      code: searchText,
      label: intl.formatMessage({
        id: 'embers.table.topControls.searchTag',
        defaultMessage: 'Search: "{searchText}"'
      }, {
        searchText: searchText
      })
    };
    return searchTag;
  };
  var createFilterTags = function createFilterTags(defaultFiltersValueMap) {
    var filterItems = [];
    if (defaultFiltersValueMap) {
      Object.keys(defaultFiltersValueMap).forEach(function (key) {
        var data = defaultFiltersValueMap[key];
        if (data) {
          if (data.selection && data.range) {
            if (data.selection.value) {
              var _option$codeKey, _option$labelKey;
              // data.selection.value fix display empty tag
              var option = getDateOption(data.range, codeKey, labelKey, dateFormatString);
              filterItems.push({
                type: 'filter',
                filterName: key,
                code: (_option$codeKey = option[codeKey]) !== null && _option$codeKey !== void 0 ? _option$codeKey : '',
                label: (_option$labelKey = option[labelKey]) !== null && _option$labelKey !== void 0 ? _option$labelKey : ''
              });
            }
          } else {
            if (!Array.isArray(data)) {
              data = [data];
            }
            data.forEach(function (option) {
              if (option[codeKey] && option[labelKey]) {
                filterItems.push({
                  type: 'filter',
                  filterName: key,
                  code: option[codeKey],
                  label: option[labelKey]
                });
              }
            });
          }
        }
      });
    }
    return filterItems;
  };
  var initFilterItems = function initFilterItems(searchText, defaultFiltersValueMap) {
    var filterItems = [];
    if (searchText) {
      filterItems.push(createSearchTag(searchText));
    }
    filterItems.push.apply(filterItems, _toConsumableArray(createFilterTags(defaultFiltersValueMap)));
    setSearchText(searchText !== null && searchText !== void 0 ? searchText : '');
    setFilterItems(filterItems);
  };
  var updateSearchTag = function updateSearchTag(searchText) {
    setSearchText(searchText);
    setFilterItems(function (preVal) {
      var newVal = [];
      if (searchText) {
        var searchTag = createSearchTag(searchText);
        if (preVal.length > 0 && preVal[0].type === 'search') {
          newVal = [searchTag].concat(_toConsumableArray(preVal.slice(1)));
        } else {
          newVal = [searchTag].concat(_toConsumableArray(preVal));
        }
      } else {
        // delete search
        newVal = preVal.filter(function (item) {
          return item.type !== 'search';
        });
      }
      return newVal;
    });
  };
  var handleSearch = function handleSearch(searchText) {
    updateSearchTag(searchText);
    onSearch === null || onSearch === void 0 || onSearch(searchText);
  };
  var updateFilterTags = function updateFilterTags(defaultFiltersValueMap) {
    setFilterItems(function (preVal) {
      var newVal = preVal.filter(function (item) {
        return item.type === 'search';
      });
      newVal.push.apply(newVal, _toConsumableArray(createFilterTags(defaultFiltersValueMap)));
      return newVal;
    });
  };
  var handleFilterChange = function handleFilterChange(_ref) {
    var filterName = _ref.filterName,
      value = _ref.value,
      reason = _ref.reason,
      option = _ref.option,
      multiple = _ref.multiple,
      isDate = _ref.isDate,
      fromModal = _ref.fromModal;
    onFilterChange === null || onFilterChange === void 0 || onFilterChange({
      filterName: filterName,
      value: value,
      fromModal: fromModal
    });
    setFilterItems(function (preVal) {
      var newVal = _toConsumableArray(preVal);
      if (reason === 'selectOption') {
        var filterItemTagIndex = -1;
        var filterItemTag = {
          type: 'filter',
          filterName: filterName,
          code: option === null || option === void 0 ? void 0 : option[codeKey],
          label: option === null || option === void 0 ? void 0 : option[labelKey]
        };
        if (!multiple) {
          filterItemTagIndex = newVal.findIndex(function (item) {
            return item.filterName === filterName;
          });
        }
        if (isDate) {
          var _dateOption$codeKey, _dateOption$labelKey;
          var newItemValue = value;
          var dateOption = getDateOption(newItemValue.range, codeKey, labelKey, dateFormatString);
          filterItemTag = {
            type: 'filter',
            filterName: filterName,
            code: (_dateOption$codeKey = dateOption[codeKey]) !== null && _dateOption$codeKey !== void 0 ? _dateOption$codeKey : '',
            label: (_dateOption$labelKey = dateOption[labelKey]) !== null && _dateOption$labelKey !== void 0 ? _dateOption$labelKey : ''
          };
        }
        if (filterItemTag) {
          if (filterItemTagIndex > -1) {
            newVal[filterItemTagIndex] = filterItemTag;
          } else {
            newVal.push(filterItemTag);
          }
        }
      } else if (reason === 'removeOption') {
        newVal = newVal.filter(function (item) {
          if (multiple) {
            return !(item.filterName === filterName && item.code === (option === null || option === void 0 ? void 0 : option[codeKey]));
          }
          return item.filterName !== filterName;
        });
      }
      return newVal;
    });
  };
  var handleTagDelete = function handleTagDelete(index) {
    if (index > -1) {
      setFilterItems(function (preVal) {
        var newVal = [].concat(_toConsumableArray(preVal.slice(0, index)), _toConsumableArray(preVal.slice(index + 1, preVal.length)));
        return newVal;
      });
      if (filterItems[index].type === 'search') {
        setSearchText('');
        onSearch === null || onSearch === void 0 || onSearch('');
      } else {
        var _filterItems$index = filterItems[index],
          _filterName = _filterItems$index.filterName,
          _code = _filterItems$index.code;
        onTagRemove(_filterName, _code);
      }
    }
  };
  var handleClearAll = function handleClearAll() {
    setFilterItems([]);
    if (filterItems[0].type === 'search') {
      setSearchText('');
    }
    onClearAll();
  };
  var handleSave = function handleSave(newFilterValue, newFilterItems) {
    var _filterItems$, _newFilterItems$;
    setFilterItems(newFilterItems);
    if (((_filterItems$ = filterItems[0]) === null || _filterItems$ === void 0 ? void 0 : _filterItems$.type) === 'search' && ((_newFilterItems$ = newFilterItems[0]) === null || _newFilterItems$ === void 0 ? void 0 : _newFilterItems$.type) !== 'search') {
      setSearchText('');
    }
    onSave === null || onSave === void 0 || onSave(newFilterValue, newFilterItems);
  };
  return {
    initFilterItems: initFilterItems,
    updateSearchTag: updateSearchTag,
    searchText: searchText,
    handleSearch: handleSearch,
    filterItems: _toConsumableArray(filterItems),
    updateFilterTags: updateFilterTags,
    handleFilterChange: handleFilterChange,
    handleTagDelete: handleTagDelete,
    handleClearAll: handleClearAll,
    handleSave: handleSave
  };
}